import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { Observable, Subject, Subscription } from 'rxjs';

import { AgentTerminalService } from './agent-terminal.service';
import { AuthService } from './auth.service';
import { ConfigService } from './config.service';

// ─── Types d'événements (tous les champs optionnels : parsing défensif) ─────
export interface RealtimeBaseEvent {
  type?: string;
  ts?: number;
  vendor_id?: number | string;
  [k: string]: unknown;
}

export interface RealtimeMessageEvent extends RealtimeBaseEvent {
  type?: 'message';
  thread_id?: number;
  thread_public_id?: string;
  /** Libellé d'expéditeur (le bus n'envoie pas d'id de message ni d'auteur). */
  sender?: string;
  /** Aperçu du corps (tronqué côté serveur). */
  body?: string;
  url?: string;
}

export interface RealtimeNotificationEvent extends RealtimeBaseEvent {
  type?: 'notification';
  title?: string;
  body?: string;
  url?: string;
}

export interface RealtimePresenceEvent extends RealtimeBaseEvent {
  type?: 'presence';
  status?: 'online' | 'offline';
  kind?: string;
  id?: number;
}

export interface RealtimeVoipCallEvent extends RealtimeBaseEvent {
  type?: 'voip_call';
  call_id?: string;
  caller?: string;
  to_ext?: string;
  direction?: string;
  display?: string;
  /** 'missed' quand l'appel n'a pas ete pris (stoppe la sonnerie). */
  event?: string;
}

export type RealtimeState = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed';

interface TicketResponse {
  ticket?: string;
  expires_in?: number;
}

interface PresenceSnapshot {
  vendor_id?: number;
  online?: Array<{ kind?: string; id?: number; ts?: number }>;
}

/** Backoff plafonné (ticket à usage unique → reconnexion maison avec ticket FRAIS). */
const BACKOFF_STEPS_MS = [1000, 2000, 5000, 10000];

const presenceKey = (kind: string | number, id: string | number): string => `${kind}:${id}`;

/**
 * RealtimeService — transport temps réel SSE M0 (chat live + notifications +
 * présence).
 *
 * Pattern aligné sur `AssistantService` : `ConfigService.apiUrl`, Bearer attaché
 * MANUELLEMENT depuis le cookie `access_token` (aucun intercepteur JWT n'est
 * enregistré ici — seul `loaderInterceptor` l'est). `EventSource` ne pouvant pas
 * poser d'en-tête, le ticket court-vécu (POST /realtime/ticket) est passé en
 * query à `GET /realtime/stream`.
 *
 * 100 % ADDITIF / NON RÉGRESSIF : tout est best-effort, aucune méthode ne lève ;
 * un flux SSE indisponible n'altère jamais le reste de l'application (les écrans
 * continuent en polling/refresh). SSR-safe : aucune connexion hors navigateur.
 */
@Injectable({ providedIn: 'root' })
export class RealtimeService {
  private readonly http = inject(HttpClient);
  private readonly cookies = inject(CookieService);
  private readonly cfg = inject(ConfigService);
  private readonly auth = inject(AuthService);
  private readonly agentTerminal = inject(AgentTerminalService);
  private readonly platformId = inject(PLATFORM_ID);

  private readonly base = `${this.cfg.apiUrl.replace(/\/+$/, '')}/realtime`;

  // ── Flux exposés ───────────────────────────────────────────────────
  private readonly messagesSubject = new Subject<RealtimeMessageEvent>();
  private readonly notificationsSubject = new Subject<RealtimeNotificationEvent>();
  private readonly presenceSubject = new Subject<RealtimePresenceEvent>();
  private readonly voipCallSubject = new Subject<RealtimeVoipCallEvent>();
  readonly messages$ = this.messagesSubject.asObservable();
  readonly notifications$ = this.notificationsSubject.asObservable();
  readonly presence$ = this.presenceSubject.asObservable();
  /** Appels VoIP entrants (reveil in-app immediat du softphone). */
  readonly voipCalls$ = this.voipCallSubject.asObservable();

  // ── État ────────────────────────────────────────────────────────────
  readonly state = signal<RealtimeState>('idle');
  readonly connected = computed<boolean>(() => this.state() === 'open');

  /** Identités en ligne du vendor (clés `kind:id`), alimentées par presence$. */
  readonly online = signal<ReadonlySet<string>>(new Set<string>());

  // ── Internes ────────────────────────────────────────────────────────
  private es: EventSource | null = null;
  private connecting = false;
  private manualClose = false;
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private ticketSub: Subscription | null = null;

  /** Vrai si l'identité `kind`/`id` est actuellement en ligne. */
  isOnline(kind: string | number, id: string | number): boolean {
    return this.online().has(presenceKey(kind, id));
  }

  /**
   * Ouvre (au plus une fois) la connexion SSE. Idempotent : no-op si déjà
   * connecté, en cours de connexion ou en attente de reconnexion. Ne lève jamais.
   */
  connect(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (typeof EventSource === 'undefined') return;
    if (this.es || this.connecting || this.reconnectTimer) return;
    this.manualClose = false;
    this.reconnectAttempt = 0;
    this.openConnection();
  }

  /**
   * Ferme proprement : annule les timers + la requête de ticket en vol, ferme
   * l'EventSource, vide la présence. Aucune connexion zombie. Ne lève jamais.
   */
  disconnect(): void {
    this.manualClose = true;
    this.connecting = false;
    this.clearReconnectTimer();
    this.cancelTicket();
    this.closeEventSource();
    this.reconnectAttempt = 0;
    this.online.set(new Set<string>());
    this.state.set('closed');
  }

  // ── Connexion ───────────────────────────────────────────────────────
  private openConnection(): void {
    if (this.manualClose || this.connecting || this.es) return;
    this.connecting = true;
    this.state.set(this.reconnectAttempt > 0 ? 'reconnecting' : 'connecting');
    this.cancelTicket();
    this.ticketSub = this.requestTicket().subscribe({
      next: (resp) => {
        this.connecting = false;
        this.ticketSub = null;
        if (this.manualClose) return;
        const ticket = resp?.ticket;
        if (!ticket) {
          this.scheduleReconnect();
          return;
        }
        this.openEventSource(ticket);
      },
      error: () => {
        this.connecting = false;
        this.ticketSub = null;
        if (this.manualClose) return;
        this.scheduleReconnect();
      },
    });
  }

  private requestTicket(): Observable<TicketResponse> {
    return this.http.post<TicketResponse>(`${this.base}/ticket`, {}, { headers: this.authHeaders() });
  }

  private openEventSource(ticket: string): void {
    try {
      const url = `${this.base}/stream?ticket=${encodeURIComponent(ticket)}`;
      const es = new EventSource(url);
      this.es = es;
      // Le backend émet TOUJOURS une ligne `event: <type>` → on écoute par type
      // (onmessage ne capterait que les events sans nom). Le PING (`: ping`) est
      // une ligne commentaire ignorée nativement par EventSource.
      es.addEventListener('ready', () => this.onReady());
      es.addEventListener('message', (ev) => this.handleMessage(ev as MessageEvent));
      es.addEventListener('notification', (ev) => this.handleNotification(ev as MessageEvent));
      es.addEventListener('presence', (ev) => this.handlePresence(ev as MessageEvent));
      es.addEventListener('voip_call', (ev) => this.handleVoipCall(ev as MessageEvent));
      es.onerror = () => this.onError();
    } catch {
      this.es = null;
      this.scheduleReconnect();
    }
  }

  private onReady(): void {
    if (this.manualClose) return;
    this.reconnectAttempt = 0;
    this.state.set('open');
    // Resynchronise l'état initial de présence (et après chaque reconnexion).
    this.refreshPresenceSnapshot();
  }

  private onError(): void {
    // NE PAS laisser EventSource rejouer le ticket (usage unique → 401 en boucle).
    this.closeEventSource();
    if (this.manualClose) return;
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    if (this.manualClose || this.reconnectTimer) return;
    const delay = BACKOFF_STEPS_MS[Math.min(this.reconnectAttempt, BACKOFF_STEPS_MS.length - 1)];
    this.reconnectAttempt += 1;
    this.state.set('reconnecting');
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.manualClose) return;
      this.openConnection();
    }, delay);
  }

  // ── Handlers d'événements (parsing défensif) ────────────────────────
  private handleMessage(ev: MessageEvent): void {
    const data = this.parse<RealtimeMessageEvent>(ev);
    if (!data) return;
    data.type = 'message';
    this.messagesSubject.next(data);
  }

  private handleNotification(ev: MessageEvent): void {
    const data = this.parse<RealtimeNotificationEvent>(ev);
    if (!data) return;
    data.type = 'notification';
    this.notificationsSubject.next(data);
  }

  private handlePresence(ev: MessageEvent): void {
    const data = this.parse<RealtimePresenceEvent>(ev);
    if (!data) return;
    data.type = 'presence';
    this.applyPresence(data);
    this.presenceSubject.next(data);
  }

  private handleVoipCall(ev: MessageEvent): void {
    const data = this.parse<RealtimeVoipCallEvent>(ev);
    if (!data) return;
    data.type = 'voip_call';
    this.voipCallSubject.next(data);
  }

  private applyPresence(ev: RealtimePresenceEvent): void {
    if (ev.kind == null || ev.id == null) return;
    const key = presenceKey(String(ev.kind), ev.id);
    this.online.update((prev) => {
      const next = new Set(prev);
      if (ev.status === 'offline') next.delete(key);
      else next.add(key);
      return next;
    });
  }

  private refreshPresenceSnapshot(): void {
    this.http
      .get<PresenceSnapshot>(`${this.base}/presence`, { headers: this.authHeaders() })
      .subscribe({
        next: (snap) => {
          const next = new Set<string>();
          for (const o of snap?.online || []) {
            if (o?.kind != null && o?.id != null) next.add(presenceKey(String(o.kind), o.id));
          }
          this.online.set(next);
        },
        error: () => undefined,
      });
  }

  // ── Helpers ─────────────────────────────────────────────────────────
  private authHeaders(): HttpHeaders {
    const token = this.cookies.get('access_token') || this.auth.getTokenFromCookies() || '';
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    // Contexte agent terrain : on joint la session POS si présente (best-effort).
    let session: string | null = null;
    try {
      session = this.agentTerminal.sessionToken();
    } catch {
      session = null;
    }
    if (session) headers['X-Agent-Session'] = session;
    return new HttpHeaders(headers);
  }

  private parse<T extends RealtimeBaseEvent>(ev: MessageEvent): T | null {
    try {
      const raw = (ev as { data?: unknown })?.data;
      if (typeof raw !== 'string' || !raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? (parsed as T) : null;
    } catch {
      return null;
    }
  }

  private closeEventSource(): void {
    if (this.es) {
      try {
        this.es.close();
      } catch {
        /* noop */
      }
      this.es = null;
    }
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private cancelTicket(): void {
    if (this.ticketSub) {
      try {
        this.ticketSub.unsubscribe();
      } catch {
        /* noop */
      }
      this.ticketSub = null;
    }
  }
}
